import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CopyableId } from '../../../shared/CopyableId';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { getSmgwHistory, SmgwHistoryResponse, SmgwHistorySeries } from '../../../../services/maasService';
import { parseUtcTimestamp, parseUtcTimestampMs } from '../../../../utils/timeUtils';
import { useHistoricalPlaybackStore } from '../../../../store/historicalPlaybackStore';
import { fmt } from '../../../../utils/numberFormat';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Props {
  isOpen: boolean;
  serialNumber: string;
  tafLogicalName?: string;
  meterLabel?: string;
  onClose: () => void;
}

type TimeRange = '24h' | '7d' | '30d' | '90d' | '365d';

// OBIS code labels for display
const obisCodeLabels: Record<string, string> = {
  '1-0:16.7.0': 'Wirkleistung (gesamt)',
  '1-0:1.7.0': 'Wirkleistung Bezug',
  '1-0:2.7.0': 'Wirkleistung Einspeisung',
  '1-0:36.7.0': 'Wirkleistung L1',
  '1-0:56.7.0': 'Wirkleistung L2',
  '1-0:76.7.0': 'Wirkleistung L3',
  '1-0:32.7.0': 'Spannung L1',
  '1-0:52.7.0': 'Spannung L2',
  '1-0:72.7.0': 'Spannung L3',
  '1-0:31.7.0': 'Strom L1',
  '1-0:51.7.0': 'Strom L2',
  '1-0:71.7.0': 'Strom L3',
  '1-0:13.7.0': 'Leistungsfaktor (gesamt)',
  '1-0:33.7.0': 'Leistungsfaktor L1',
  '1-0:53.7.0': 'Leistungsfaktor L2',
  '1-0:73.7.0': 'Leistungsfaktor L3',
  '1-0:14.7.0': 'Frequenz',
};

// Category for grouping OBIS codes
const getObisCategory = (obisCode: string): string => {
  if (obisCode.includes(':16.') || obisCode.includes(':1.') || obisCode.includes(':2.') ||
      obisCode.includes(':36.') || obisCode.includes(':56.') || obisCode.includes(':76.')) {
    return 'power';
  }
  if (obisCode.includes(':32.') || obisCode.includes(':52.') || obisCode.includes(':72.')) {
    return 'voltage';
  }
  if (obisCode.includes(':31.') || obisCode.includes(':51.') || obisCode.includes(':71.')) {
    return 'current';
  }
  if (obisCode.includes(':13.') || obisCode.includes(':33.') || obisCode.includes(':53.') || obisCode.includes(':73.')) {
    return 'powerfactor';
  }
  if (obisCode.includes(':14.')) {
    return 'frequency';
  }
  return 'other';
};

// Colors for different series
const seriesColors = [
  { border: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.1)' },   // Blue
  { border: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)' },     // Red
  { border: 'rgb(234, 179, 8)', bg: 'rgba(234, 179, 8, 0.1)' },     // Yellow
  { border: 'rgb(34, 197, 94)', bg: 'rgba(34, 197, 94, 0.1)' },     // Green
  { border: 'rgb(139, 92, 246)', bg: 'rgba(139, 92, 246, 0.1)' },   // Purple
  { border: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.1)' },   // Orange
];

interface ChartSectionProps {
  title: string;
  series: SmgwHistorySeries[];
  unit: string;
}

interface SeriesPeaks {
  rangeMin: number;
  rangeMax: number;
  rangeAvg: number;
  minAt: string;
  maxAt: string;
  /** Σ avg × bucket length — energy over the window (Wh) for power series. */
  energyWh: number;
  /** Bucket length of the series points in hours (1 = hourly, 24 = daily aggregation). */
  bucketHours: number;
}

/**
 * Bucket length of a series from the median spacing of its points: the
 * backend serves hourly points for short windows and daily aggregates beyond
 * ~45 days (SmgwHistory bucketing). Daily buckets change the reading of Ø:
 * an average over 24 h incl. the night, next to a Max that is the single
 * most extreme 15-min value of the day.
 */
const seriesBucketHours = (points: { timestamp: string }[]): number => {
  if (points.length < 2) return 1;
  const gaps: number[] = [];
  for (let i = 1; i < Math.min(points.length, 50); i++) {
    const g = (parseUtcTimestampMs(points[i].timestamp) - parseUtcTimestampMs(points[i - 1].timestamp)) / 3_600_000;
    if (g > 0) gaps.push(g);
  }
  if (gaps.length === 0) return 1;
  gaps.sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)];
  return median >= 20 ? 24 : 1;
};

const computeSeriesPeaks = (s: SmgwHistorySeries): SeriesPeaks | null => {
  if (s.dataPoints.length === 0) return null;

  let rangeMin = s.dataPoints[0].minValue;
  let rangeMax = s.dataPoints[0].maxValue;
  let minAt = s.dataPoints[0].timestamp;
  let maxAt = s.dataPoints[0].timestamp;
  let weightedSum = 0;
  let totalCount = 0;
  const bucketHours = seriesBucketHours(s.dataPoints);
  let energyWh = 0;

  for (const dp of s.dataPoints) {
    energyWh += dp.avgValue * bucketHours;
    if (dp.minValue < rangeMin) {
      rangeMin = dp.minValue;
      minAt = dp.timestamp;
    }
    if (dp.maxValue > rangeMax) {
      rangeMax = dp.maxValue;
      maxAt = dp.timestamp;
    }
    weightedSum += dp.avgValue * dp.measurementCount;
    totalCount += dp.measurementCount;
  }

  return {
    rangeMin,
    rangeMax,
    rangeAvg: totalCount > 0 ? weightedSum / totalCount : 0,
    minAt,
    maxAt,
    energyWh,
    bucketHours,
  };
};

const formatEnergyKwh = (wh: number): string => {
  const kwh = wh / 1000;
  return Math.abs(kwh) >= 100 ? fmt(kwh, 0) : fmt(kwh, 1);
};

const formatPeakTimestamp = (iso: string): string => {
  const d = parseUtcTimestamp(iso);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatValue = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1000) return fmt(value, 0);
  if (abs >= 10) return fmt(value, 1);
  return fmt(value, 2);
};

interface PeakSummaryTableProps {
  series: SmgwHistorySeries[];
  unit: string;
}

const PeakSummaryTable: React.FC<PeakSummaryTableProps> = ({ series, unit }) => {
  const rows = useMemo(
    () =>
      series
        .map((s, idx) => ({
          series: s,
          colors: seriesColors[idx % seriesColors.length],
          peaks: computeSeriesPeaks(s),
        }))
        .filter((r): r is { series: SmgwHistorySeries; colors: typeof seriesColors[number]; peaks: SeriesPeaks } =>
          r.peaks !== null
        ),
    [series]
  );

  if (rows.length === 0) return null;

  const unitSuffix = unit ? ` ${unit}` : '';
  const isPower = unit === 'W';
  const daily = rows.some(r => r.peaks.bucketHours >= 24);
  const avgTitle = daily
    ? 'Mittelwert über den gesamten Zeitraum inkl. Nacht (Tagesaggregation) — bei Einspeisung typischerweise klein gegenüber dem Max, weil Überschuss nur zeitweise anfällt'
    : 'Mittelwert über den gesamten Zeitraum';

  return (
    <div className="mb-2 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 dark:text-primary-400">
            <th className="text-left font-medium py-1 pr-3">Messgröße</th>
            <th className="text-right font-medium py-1 px-2" title={daily ? 'Niedrigster Tageswert (extremste Viertelstunde des Tages)' : 'Niedrigster Wert im Zeitraum'}>Min</th>
            <th className="text-left font-normal py-1 pr-3 text-gray-400 dark:text-primary-500">
              <span className="sr-only">Min-Zeitpunkt</span>
            </th>
            <th className="text-right font-medium py-1 px-2" title={daily ? 'Höchster Tageswert (extremste Viertelstunde des Tages)' : 'Höchster Wert im Zeitraum'}>Max</th>
            <th className="text-left font-normal py-1 pr-3 text-gray-400 dark:text-primary-500">
              <span className="sr-only">Max-Zeitpunkt</span>
            </th>
            <th className="text-right font-medium py-1 pl-2" title={avgTitle}>Ø</th>
            {isPower && (
              <th className="text-right font-medium py-1 pl-3" title="Energie im Zeitraum (Σ Mittelwert × Intervall) — Bezug bzw. Einspeisung in kWh">Σ Energie</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ series: s, colors, peaks }) => (
            <tr
              key={s.obisCode}
              className="border-t border-gray-100 dark:border-primary-700"
            >
              <td className="py-1.5 pr-3 text-gray-700 dark:text-primary-200">
                <span className="inline-flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: colors.border }}
                  />
                  {obisCodeLabels[s.obisCode] || s.obisCode}
                </span>
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums text-error-600 dark:text-error-400 font-medium">
                {formatValue(peaks.rangeMin)}
                {unitSuffix}
              </td>
              <td className="py-1.5 pr-3 text-gray-400 dark:text-primary-500 tabular-nums">
                {formatPeakTimestamp(peaks.minAt)}
              </td>
              <td className="py-1.5 px-2 text-right tabular-nums text-success-600 dark:text-success-400 font-medium">
                {formatValue(peaks.rangeMax)}
                {unitSuffix}
              </td>
              <td className="py-1.5 pr-3 text-gray-400 dark:text-primary-500 tabular-nums">
                {formatPeakTimestamp(peaks.maxAt)}
              </td>
              <td className="py-1.5 pl-2 text-right tabular-nums text-gray-600 dark:text-primary-300">
                {formatValue(peaks.rangeAvg)}
                {unitSuffix}
              </td>
              {isPower && (
                <td className="py-1.5 pl-3 text-right tabular-nums text-gray-800 dark:text-primary-100 font-medium">
                  {formatEnergyKwh(peaks.energyWh)} kWh
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {daily && (
        <div className="mt-1 text-[10px] text-gray-500 dark:text-primary-400">
          Tagesaggregation: Min/Max sind die extremsten Viertelstundenwerte je Tag, Ø ist der Mittelwert über 24 h inklusive Nacht.
          {isPower && ' Bei Einspeise-Registern liegt Ø deshalb oft nahe 0, obwohl kurze Überschussspitzen hoch sind — die Σ Energie ist die belastbare Kennzahl.'}
        </div>
      )}
    </div>
  );
};

const ChartSection: React.FC<ChartSectionProps> = ({ title, series, unit }) => {
  const chartData = useMemo(() => {
    if (series.length === 0) return null;

    // Collect all timestamps
    const timestampSet = new Set<string>();
    series.forEach(s => {
      s.dataPoints.forEach(dp => timestampSet.add(dp.timestamp));
    });

    const timestamps = Array.from(timestampSet).sort((a, b) =>
      parseUtcTimestampMs(a) - parseUtcTimestampMs(b)
    );

    if (timestamps.length === 0) return null;

    // Create datasets: for each series, 3 datasets (min, max with fill to min, avg)
    const datasets: Array<{
      label: string;
      data: (number | null)[];
      borderColor: string;
      backgroundColor: string;
      tension: number;
      pointRadius: number;
      borderWidth: number;
      fill: boolean | { target: number; above: string };
      borderDash?: number[];
      isMinMax?: boolean;
    }> = [];

    series.forEach((s, idx) => {
      const colors = seriesColors[idx % seriesColors.length];
      const pointMap = new Map(s.dataPoints.map(dp => [dp.timestamp, dp]));
      const baseIndex = idx * 3;

      // Min dataset (invisible line, lower bound of band)
      datasets.push({
        label: `${obisCodeLabels[s.obisCode] || s.obisCode} (Min)`,
        data: timestamps.map(ts => pointMap.get(ts)?.minValue ?? null),
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 0,
        fill: false,
        isMinMax: true,
      });

      // Max dataset (invisible line, fills down to min)
      datasets.push({
        label: `${obisCodeLabels[s.obisCode] || s.obisCode} (Max)`,
        data: timestamps.map(ts => pointMap.get(ts)?.maxValue ?? null),
        borderColor: 'transparent',
        backgroundColor: colors.bg,
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 0,
        fill: { target: baseIndex, above: colors.bg },
        isMinMax: true,
      });

      // Avg dataset (visible solid line)
      datasets.push({
        label: obisCodeLabels[s.obisCode] || s.obisCode,
        data: timestamps.map(ts => pointMap.get(ts)?.avgValue ?? null),
        borderColor: colors.border,
        backgroundColor: colors.border,
        tension: 0.3,
        pointRadius: 1,
        borderWidth: 2,
        fill: false,
        isMinMax: false,
      });
    });

    return {
      labels: timestamps.map(ts => {
        const d = parseUtcTimestamp(ts);
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
      }),
      datasets,
    };
  }, [series]);

  if (!chartData) return null;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 8,
          padding: 10,
          font: { size: 10 },
          filter: (item: { datasetIndex?: number }) => {
            // Only show avg datasets in legend (every 3rd, starting at index 2)
            return (item.datasetIndex ?? 0) % 3 === 2;
          },
        },
      },
      tooltip: {
        filter: (item: TooltipItem<'line'>) => {
          return !(item.dataset as { isMinMax?: boolean }).isMinMax;
        },
        callbacks: {
          label: (context: TooltipItem<'line'>) => {
            // Skip min/max datasets in tooltip - they're shown with their avg line
            if ((context.dataset as { isMinMax?: boolean }).isMinMax) return '';

            const label = context.dataset.label || '';
            const avg = context.parsed.y;
            // Min is 2 datasets before avg, max is 1 dataset before
            const minDataset = chartData!.datasets[context.datasetIndex - 2];
            const maxDataset = chartData!.datasets[context.datasetIndex - 1];
            const min = minDataset?.data[context.dataIndex];
            const max = maxDataset?.data[context.dataIndex];

            if (min != null && max != null) {
              return `${label}: ${fmt((avg ?? 0), 2)} ${unit}  (Min: ${fmt(Number(min), 2)} | Max: ${fmt(Number(max), 2)})`;
            }
            return `${label}: ${fmt((avg ?? 0), 2)} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxTicksLimit: 8, font: { size: 9 } },
      },
      y: {
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          font: { size: 9 },
          callback: (value: string | number) => `${value} ${unit}`,
        },
      },
    },
  };

  return (
    <div className="mb-6">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{title}</h4>
      <PeakSummaryTable series={series} unit={unit} />
      <div className="h-48 bg-gray-50 dark:bg-primary-700 rounded-lg p-2">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export const SmgwHistoryModal: React.FC<Props> = ({
  isOpen,
  serialNumber,
  tafLogicalName,
  meterLabel,
  onClose
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [historyData, setHistoryData] = useState<SmgwHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // In historical playback the window ends at the selected instant, not now —
  // otherwise archived zones (data ends in the past) show empty short ranges.
  const playbackMode = useHistoricalPlaybackStore(state => state.mode);
  const effectiveTs = useHistoricalPlaybackStore(state => state.effectiveTs);
  const anchorIso = playbackMode === 'historical' && effectiveTs ? effectiveTs : undefined;

  useEffect(() => {
    if (!isOpen || !serialNumber) return;

    const fetchHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getSmgwHistory(serialNumber, timeRange, tafLogicalName, anchorIso);
        setHistoryData(data);
      } catch (err) {
        console.error('Error fetching SMGW history:', err);
        setError('Fehler beim Laden der historischen Daten');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen, serialNumber, tafLogicalName, timeRange, anchorIso]);

  // Group series by category
  const groupedSeries = useMemo(() => {
    if (!historyData) return null;

    const groups: Record<string, SmgwHistorySeries[]> = {
      power: [],
      voltage: [],
      current: [],
      powerfactor: [],
      frequency: [],
      other: [],
    };

    historyData.series.forEach(s => {
      const category = getObisCategory(s.obisCode);
      groups[category].push(s);
    });

    return groups;
  }, [historyData]);

  if (!isOpen) return null;

  const hasData = historyData && historyData.series.length > 0;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-9999">
      <div className="bg-white dark:bg-primary-800 rounded-xl shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 bg-linear-to-r from-success-500/10 dark:from-success-500/20 to-white dark:to-primary-800 border-b border-gray-300 dark:border-primary-600 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success-500/20 dark:bg-success-500/30 rounded-lg">
              <svg className="w-5 h-5 text-success-600 dark:text-success-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-primary-50">
                Historische Messdaten
                {meterLabel && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-primary-400">
                    - {meterLabel}
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-500 dark:text-primary-400">
                SMGW: <CopyableId value={serialNumber} title="SMGW-Seriennummer" className="text-xs" />
                {tafLogicalName && (
                  <span className="ml-2">| Zähler: <CopyableId value={tafLogicalName} className="text-xs" /></span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-primary-700 rounded-lg transition-colors"
            aria-label="Schließen"
          >
            <svg className="w-5 h-5 text-gray-500 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Time Range Selector */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-primary-600 flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Zeitraum:</span>
          {(['24h', '7d', '30d', '90d', '365d'] as TimeRange[]).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                timeRange === range
                  ? 'bg-success-500 text-white'
                  : 'bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-primary-600'
              }`}
            >
              {range === '24h' ? '24 Stunden' : range === '7d' ? '7 Tage' : range === '30d' ? '30 Tage' : range === '90d' ? '90 Tage' : '1 Jahr'}
            </button>
          ))}
          {anchorIso && (
            <span className="ml-auto text-xs text-orange-600 dark:text-orange-300" title="Historische Ansicht: Das Zeitfenster endet am gewählten historischen Zeitpunkt.">
              bis {formatPeakTimestamp(anchorIso)} Uhr
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-success-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          {!isLoading && !error && !hasData && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p>Keine historischen Daten verfügbar</p>
              <p className="text-sm mt-1">Die Daten werden erst nach einiger Zeit aggregiert.</p>
            </div>
          )}

          {!isLoading && !error && hasData && groupedSeries && (
            <div>
              {/* Charts by category */}
              {groupedSeries.power.length > 0 && (
                <ChartSection title="Wirkleistung" series={groupedSeries.power} unit="W" />
              )}
              {groupedSeries.voltage.length > 0 && (
                <ChartSection title="Spannung" series={groupedSeries.voltage} unit="V" />
              )}
              {groupedSeries.current.length > 0 && (
                <ChartSection title="Strom" series={groupedSeries.current} unit="A" />
              )}
              {groupedSeries.powerfactor.length > 0 && (
                <ChartSection title="Leistungsfaktor" series={groupedSeries.powerfactor} unit="" />
              )}
              {groupedSeries.frequency.length > 0 && (
                <ChartSection title="Frequenz" series={groupedSeries.frequency} unit="Hz" />
              )}
              {groupedSeries.other.length > 0 && (
                <ChartSection title="Sonstige" series={groupedSeries.other} unit="" />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-primary-600 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-primary-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-primary-600 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SmgwHistoryModal;
